import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonVariants, Icon, IconNames } from 'atoms';
import { ICustomError, ICard, CardPosition, ICardsMap } from 'types';
import { errorMessages } from 'constants/common';
import { Popup } from '../Popup';
import {
  ButtonsWrapper,
  CardGroup,
  CardRow,
  CardSelectionWrapper,
  ClearButton,
  ConfirmButton,
  PickerGrid,
  PickerRow,
  PickerCell,
  PickerItem,
  ResultsWrapper,
  SelectRow,
  SideLabel,
  CardSlot,
  RotatedCardWrapper,
} from './styles';
import { ErrorMessage } from '../PopupVerification/styles';
import {
  CardSlotVariants,
  BANKER_ORDER,
  PLAYER_ORDER,
  RED_SUITS,
  Sides,
} from '../ClassicBaccaratResult/ListCards';

const RANKS = ['ace', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'jack', 'queen', 'king'];
const SUITS: Array<'diamonds' | 'hearts' | 'clubs' | 'spades'> = [
  'diamonds',
  'clubs',
  'hearts',
  'spades',
];

const MAIN_SLOTS_ORDER: CardPosition[] = ['1', '2', '3', '4'];

const getRankLabel = (rank: string) => (rank === '10' ? '10' : rank.charAt(0).toUpperCase());

const getRankIcon = (suit: string, rank: string) => {
  const color = RED_SUITS.includes(suit) ? 'red' : 'black';
  return `${color}_rank_${rank}` as IconNames;
};

interface IProps {
  loading: boolean;
  onClose: () => void;
  onConfirmClick: (cards: ICardsMap) => void;
  error?: ICustomError;
}

const initialState: ICardsMap = {};

export const ManualCardInput = ({ loading, onClose, onConfirmClick, error }: IProps) => {
  const [cards, setCards] = useState<ICardsMap>(initialState);
  const [activeSlot, setActiveSlot] = useState<CardPosition | null>('1');
  const [selectedRank, setSelectedRank] = useState<string | null>(null);
  const [selectedSuit, setSelectedSuit] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setSelectedRank(null);
        setSelectedSuit(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onClearClick = useCallback(() => {
    setCards(initialState);
    setActiveSlot('1');
    setSelectedRank(null);
    setSelectedSuit(null);
  }, []);

  const handleSlotSelect = useCallback(
    (position: CardPosition) => {
      const isMainSlotsFilled = cards[1] && cards[2] && cards[3] && cards[4];
      if ((position !== '5' && position !== '6') || !isMainSlotsFilled) return;

      setActiveSlot(position);
      setSelectedRank(null);
      setSelectedSuit(null);
    },
    [cards],
  );

  const applyCard = useCallback(
    (rank: string, suit: string) => {
      if (!activeSlot) return;
      const card: ICard = { rank, suit };
      const nextCards: ICardsMap = { ...cards, [activeSlot]: card };
      setCards(nextCards);

      if (activeSlot === '5' || activeSlot === '6') {
        setActiveSlot(null);
        setSelectedRank(null);
        setSelectedSuit(null);
        return;
      }

      const currentIdx = MAIN_SLOTS_ORDER.indexOf(activeSlot);
      if (currentIdx !== -1) {
        const nextPosition = MAIN_SLOTS_ORDER.slice(currentIdx + 1).find(
          (position) => !nextCards[position],
        );
        setActiveSlot(nextPosition || null);
        setSelectedRank(null);
        setSelectedSuit(null);
      }
    },
    [activeSlot, cards],
  );

  const handleRankChange = useCallback(
    (rank: string) => {
      setSelectedRank(rank);
      if (selectedSuit) applyCard(rank, selectedSuit);
    },
    [selectedSuit, applyCard],
  );

  const handleSuitChange = useCallback(
    (suit: string) => {
      setSelectedSuit(suit);
      if (selectedRank) applyCard(selectedRank, suit);
    },
    [selectedRank, applyCard],
  );

  const renderSlot = (position: CardPosition, side: Sides) => {
    const card = cards[position];
    const isThirdCard = position === '5' || position === '6';
    const isActive = activeSlot === position;

    const slotContent = () => {
      if (card) {
        return (
          <CardSlot $filled>
            <Icon name={getRankIcon(card.suit, card.rank)} />
            <Icon name={card.suit as IconNames} />
          </CardSlot>
        );
      }
      return (
        <CardSlot
          $variant={CardSlotVariants.Empty}
          $active={isActive}
          $clickable={isThirdCard}
          onClick={() => handleSlotSelect(position)}
        />
      );
    };

    if (isThirdCard) {
      const rotatedSide = side === Sides.Banker ? 'left' : 'right';
      return (
        <RotatedCardWrapper key={position} $side={rotatedSide}>
          {slotContent()}
        </RotatedCardWrapper>
      );
    }

    return <React.Fragment key={position}>{slotContent()}</React.Fragment>;
  };

  const renderCards = () => (
    <ResultsWrapper>
      <CardGroup>
        <CardRow>{BANKER_ORDER.map((pos: CardPosition) => renderSlot(pos, Sides.Banker))}</CardRow>
        <SideLabel $align="left">Banker</SideLabel>
      </CardGroup>
      <CardGroup>
        <CardRow>{PLAYER_ORDER.map((pos: CardPosition) => renderSlot(pos, Sides.Player))}</CardRow>
        <SideLabel $align="right">Player</SideLabel>
      </CardGroup>
    </ResultsWrapper>
  );

  return (
    <Popup title="Manual card input" onClose={onClose} loading={loading}>
      {renderCards()}
      <CardSelectionWrapper ref={pickerRef}>
        <SelectRow>
          <PickerGrid>
            <tbody>
              <PickerRow>
                {RANKS.slice(0, 5).map((rank) => (
                  <PickerCell key={rank}>
                    <PickerItem
                      $active={selectedRank === rank}
                      onClick={() => handleRankChange(rank)}>
                      {getRankLabel(rank)}
                    </PickerItem>
                  </PickerCell>
                ))}
              </PickerRow>
              <PickerRow>
                {RANKS.slice(5, 10).map((rank) => (
                  <PickerCell key={rank}>
                    <PickerItem
                      $active={selectedRank === rank}
                      onClick={() => handleRankChange(rank)}>
                      {getRankLabel(rank)}
                    </PickerItem>
                  </PickerCell>
                ))}
              </PickerRow>
              <PickerRow>
                {RANKS.slice(10).map((rank) => (
                  <PickerCell key={rank}>
                    <PickerItem
                      $ignoreRoundBorder={rank === 'king'}
                      $active={selectedRank === rank}
                      onClick={() => handleRankChange(rank)}>
                      {getRankLabel(rank)}
                    </PickerItem>
                  </PickerCell>
                ))}
              </PickerRow>
            </tbody>
          </PickerGrid>
        </SelectRow>
        <SelectRow>
          <PickerGrid>
            <tbody>
              <PickerRow>
                {SUITS.slice(0, 2).map((suit) => (
                  <PickerCell key={suit}>
                    <PickerItem
                      $active={selectedSuit === suit}
                      $color={RED_SUITS.includes(suit) ? 'red' : 'black'}
                      onClick={() => handleSuitChange(suit)}>
                      <Icon name={suit as IconNames} />
                    </PickerItem>
                  </PickerCell>
                ))}
              </PickerRow>
              <PickerRow>
                {SUITS.slice(2).map((suit) => (
                  <PickerCell key={suit}>
                    <PickerItem
                      $active={selectedSuit === suit}
                      $color={RED_SUITS.includes(suit) ? 'red' : 'black'}
                      onClick={() => handleSuitChange(suit)}>
                      <Icon name={suit as IconNames} />
                    </PickerItem>
                  </PickerCell>
                ))}
              </PickerRow>
            </tbody>
          </PickerGrid>
        </SelectRow>
      </CardSelectionWrapper>
      {error ? (
        <ErrorMessage>
          {error.status === 601 ? errorMessages.NotAvailableAction : error.message}
        </ErrorMessage>
      ) : null}
      <ButtonsWrapper>
        <ConfirmButton disabled={false} loading={loading} onClick={() => onConfirmClick(cards)}>
          Confirm
        </ConfirmButton>
        <ClearButton variant={ButtonVariants.Cancel} disabled={loading} onClick={onClearClick}>
          Clear
        </ClearButton>
      </ButtonsWrapper>
    </Popup>
  );
};
